import { useEffect, useState } from "react"
import { Table } from "reactstrap"
import Modal from "Components/molecule/modal"
import ActionButton from "Components/atoms/buttons/action-button"
import TextInput from "Components/molecule/inputs/text-input"
import { BillAllocation, ExpenseBudgetLine, VendorBill } from "models/expense-budget"
import { planWeights, splitByPlan, sumAmounts } from "utils/expense-budget"
import { formatCurrency } from "utils/tools"

interface BillSplitModalProps {
    bill: VendorBill | null
    products: ExpenseBudgetLine[]
    allocations: BillAllocation[]
    onClose: () => void
    onApply: (allocations: BillAllocation[]) => void
}

// The split must add up to the bill line amount, give or take rounding cents.
const TOLERANCE = 0.01

// Splits one bill line across several products of the vendor. Ticking a product redistributes the
// amount by plan; each amount can then be edited.
const BillSplitModal = ({ bill, products, allocations, onClose, onApply }: BillSplitModalProps) => {
    const [amounts, setAmounts] = useState<Record<string, string>>({})

    useEffect(() => {
        if (!bill) return
        setAmounts(Object.fromEntries(allocations.map(a => [a.expenseBudgetLineId, String(a.amount)])))
    }, [bill])

    if (!bill) return null

    const selectedIds = products.filter(product => amounts[product.id] !== undefined).map(product => product.id)
    const allocated = sumAmounts(selectedIds.map(id => Number(amounts[id]) || 0))
    const remaining = Math.round((bill.amount - allocated) * 100) / 100
    const canApply = selectedIds.length > 0 && Math.abs(remaining) <= TOLERANCE

    const distribute = (lineIds: string[]) => {
        const chosen = products.filter(product => lineIds.includes(product.id))
        const split = splitByPlan(bill.amount, planWeights(chosen, bill.fiscalPeriod))
        setAmounts(Object.fromEntries(chosen.map((product, i) => [product.id, String(split[i])])))
    }

    const toggle = (lineId: string) => distribute(selectedIds.includes(lineId)
        ? selectedIds.filter(id => id !== lineId)
        : [...selectedIds, lineId])

    const apply = () => onApply(selectedIds.map(id => ({ expenseBudgetLineId: id, amount: Number(amounts[id]) || 0 })))

    return (
        <Modal isOpen onClose={onClose} title="Split bill line"
            subtitle={`${bill.refNbr} · ${bill.lineDescription ?? bill.docDescription ?? ""} · ${formatCurrency(bill.amount)}`}>
            <div className="d-flex flex-column gap-2">
                <Table size="sm" className="align-middle mb-0">
                    <thead>
                        <tr><th /><th>Product</th><th className="text-end" style={{ width: 160 }}>Amount</th></tr>
                    </thead>
                    <tbody>
                        {products.map(product => {
                            const isSelected = selectedIds.includes(product.id)
                            return (
                                <tr key={product.id}>
                                    <td><input type="checkbox" className="form-check-input" checked={isSelected} onChange={() => toggle(product.id)} /></td>
                                    <td>{product.productName}</td>
                                    <td>
                                        {isSelected && <TextInput type="number" step="0.01" value={amounts[product.id]}
                                            onChange={e => setAmounts(prev => ({ ...prev, [product.id]: e.target.value }))} />}
                                    </td>
                                </tr>
                            )
                        })}
                    </tbody>
                </Table>
                <div className="d-flex justify-content-between align-items-center">
                    <span className={Math.abs(remaining) <= TOLERANCE ? "text-success" : "text-danger"}>
                        Allocated {formatCurrency(allocated)} · Remaining {formatCurrency(remaining)}
                    </span>
                    <ActionButton disabled={selectedIds.length === 0} onClick={() => distribute(selectedIds)}>Distribute by plan</ActionButton>
                </div>
                <div className="d-flex justify-content-end gap-2">
                    <ActionButton onClick={onClose}>Cancel</ActionButton>
                    <ActionButton disabled={!canApply} onClick={apply}>Apply</ActionButton>
                </div>
            </div>
        </Modal>
    )
}

export default BillSplitModal
